import React, {useCallback, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {ActionCreators} from 'redux-undo';
import HighlightedInputMedia from '../../../../../../components/fileLoader/input.media';
import useNblFileReader from '../../../../../../components/fileLoader/util/useNblFileReader';
import {SlideElementHotkeys} from '../../../../../../constants/slideElementHotkeys';
import Button from '../../../../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactRef,
    DROPDOWN_MENU_COMPACT_SUBMENU_HOVER_OPEN_CLOSE_MS,
} from '../../../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {
    highlightedElementIndexSelector,
    isSlideModifiedSelector,
    selectedSlideSelector,
    slideReducerFutureSelector,
    slideReducerPastSelector,
} from '../../../../../../store/selectors/slide.selectors';
import useElementActions from '../../../../../../utils/hooks/slideUpdates/useElementActions';
import {useRulesModal} from '../../../../../context/RulesModalContext';
import {useSlideUpdateContext} from '../../../../../context/SetSlideUpdateContext';

type MenuBarEditDropdownProps = {
    editMenuItemRef: React.RefObject<DropdownMenuCompactRef>;
    onHoverEnter: () => void;
};

function MenuBarEditDropdown({editMenuItemRef, onHoverEnter}: MenuBarEditDropdownProps): JSX.Element {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {openRulesModal} = useRulesModal();
    const {setAndSaveSlide} = useSlideUpdateContext();
    const {importElement, exportElement, bringToFront, sendToBack} = useElementActions();

    const selectedSlide = useSelector(selectedSlideSelector);
    const isSlideModified = useSelector(isSlideModifiedSelector);
    const highlightedElementIndex = useSelector(highlightedElementIndexSelector);
    const slidePast = useSelector(slideReducerPastSelector);
    const slideFuture = useSelector(slideReducerFutureSelector);

    const nblFileInputRef = useRef<HTMLInputElement>(null);
    const getSlide = useCallback(() => selectedSlide, [selectedSlide]);
    const nblFileReader = useNblFileReader((elements) => {
        elements.forEach(importElement);
    }, getSlide);

    const mutateOnModified = useCallback(
        (cb?: () => void): void => {
            if (isSlideModified) {
                setAndSaveSlide(selectedSlide, {onSuccess: cb});
            } else cb?.();
        },
        [isSlideModified, selectedSlide, setAndSaveSlide]
    );

    const editMenuItems = useMemo(
        () => [
            [
                {
                    label: t('menuBar.editMenu.undo'),
                    icon: 'solar:undo-left-round-linear',
                    disabled: slidePast.length < 1,
                    onClick: () => dispatch(ActionCreators.undo()),
                    shortcuts: SlideElementHotkeys.Undo.getPlatformKeys(window.navigator),
                },
                {
                    label: t('menuBar.editMenu.redo'),
                    icon: 'solar:undo-right-round-linear',
                    disabled: !(slideFuture.length > 0),
                    onClick: () => dispatch(ActionCreators.redo()),
                    shortcuts: SlideElementHotkeys.Redo.getPlatformKeys(window.navigator),
                },
            ],
            [
                {
                    label: t('menuBar.editMenu.bringToFront'),
                    icon: 'solar:notes-linear',
                    disabled: highlightedElementIndex < 0,
                    onClick: () => {
                        bringToFront(highlightedElementIndex);
                    },
                },
                {
                    label: t('menuBar.editMenu.sendToBack'),
                    icon: 'solar:notes-minimalistic-linear',
                    disabled: highlightedElementIndex < 0,
                    onClick: () => {
                        sendToBack(highlightedElementIndex);
                    },
                },
            ],
            [
                {
                    label: t('menuBar.editMenu.exportElement'),
                    icon: 'solar:download-minimalistic-linear',
                    disabled: highlightedElementIndex < 0,
                    onClick: () => {
                        exportElement(highlightedElementIndex);
                    },
                    shortcuts: SlideElementHotkeys.Export.getPlatformKeys(window.navigator),
                },
                {
                    label: t('menuBar.editMenu.importElement'),
                    icon: 'solar:upload-minimalistic-linear',
                    onClick: () => {
                        nblFileInputRef.current?.click();
                    },
                    shortcuts: SlideElementHotkeys.Import.getPlatformKeys(window.navigator),
                },
            ],
            [
                {
                    label: t('menuBar.editMenu.rules'),
                    icon: 'solar:reorder-linear',
                    onClick: () => mutateOnModified(openRulesModal),
                },
            ],
        ],
        [
            t,
            dispatch,
            slidePast.length,
            slideFuture.length,
            highlightedElementIndex,
            bringToFront,
            sendToBack,
            exportElement,
            mutateOnModified,
            openRulesModal,
        ]
    );

    return (
        <>
            <DropdownMenuCompact
                ref={editMenuItemRef}
                openOnMouseEnter
                closeOnMouseExit
                closeOnMouseExitDelayMs={DROPDOWN_MENU_COMPACT_SUBMENU_HOVER_OPEN_CLOSE_MS}
                onHoverEnter={onHoverEnter}
                items={editMenuItems}
            >
                <Button size="2xs" disableHover square variant="ghost" className="c-menuBar__button">
                    {t('menuBar.edit')}
                </Button>
            </DropdownMenuCompact>
            <div className="u-d-none">
                <HighlightedInputMedia type="nbl" inputRef={nblFileInputRef} onLoad={nblFileReader} />
            </div>
        </>
    );
}

export default React.memo(MenuBarEditDropdown);
